<script setup lang="ts">
import LeButton from "@dsk/components/Button/LeButton.vue";
import LeCheckbox from "@dsk/components/Checkbox/LeCheckbox.vue";
import LeInput from "@dsk/components/Form/LeInput.vue";
import LeHeading from "@dsk/components/Heading/LeHeading.vue";
import LeModal from "@dsk/components/LeModal.vue";
import type { ModalProps } from "@dsk/types";
import { useField, useForm } from "vee-validate";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { config } from "@/config";
import { ValidationHelper } from "@/helpers";
import { FeedbackService } from "@/services";
import { sleep } from "@/utils";
import { feedbackFormValidator } from "@/validation";

import LeFeedbackModalStars from "./LeFeedbackModalStars.vue";

defineProps<ModalProps>();

const emit = defineEmits<{
  close: [didSubmit: boolean];
}>();

const { t } = useI18n();

const { language, site } = config.app;

const { handleSubmit, resetForm } = useForm({
  initialValues: {
    rating: 0,
    comment: undefined,
    email: undefined,
    canContact: undefined
  },
  validationSchema: feedbackFormValidator
});

const { value: rating } = useField<number>("rating");
const { value: comment, meta: commentMeta } = useField<string | undefined>("comment");
const { value: email, meta: emailMeta, setValue: setEmailValue } = useField<string | undefined>("email");
const { value: canContact, meta: canContactMeta } = useField<boolean | undefined>("canContact");

const hasApiError = ref<boolean>(false);

const hasRated = computed<boolean>(() => rating.value > 0);
const hasCommented = computed<boolean>(() => (comment.value ? comment.value.length > 0 : false));

const commentHeading = computed<string>(() => {
  if (rating.value === 5) {
    return t("le-feedback-modal.comment-heading.perfect");
  }

  return t("le-feedback-modal.comment-heading.default");
});

watch(
  () => email.value,
  (newValue) => {
    if (newValue === "") {
      setEmailValue(undefined);
    }
  }
);

const onClose = () => {
  if (rating.value) {
    FeedbackService.sendFeedback({ rating: rating.value });
    delayedResetForm();
  }

  emit("close", false);
};

const onSubmit = handleSubmit(async (form) => {
  hasApiError.value = false;

  try {
    await FeedbackService.sendFeedback(form);
  } catch (e) {
    hasApiError.value = true;
  }

  delayedResetForm();
  emit("close", true);
});

const delayedResetForm = async () => {
  // Wait for modal transition
  await sleep(150);
  resetForm();
};
</script>

<template>
  <LeModal class="le-feedback-modal" :is-open="isOpen" @close="onClose()">
    <template #title>
      <LeHeading tag="h2" size="m">{{ t("le-feedback-modal.title") }}</LeHeading>
    </template>

    <template #body>
      <form id="feedback-form" @submit.prevent="onSubmit()">
        <section class="le-feedback-modal__section le-feedback-modal__section-rating">
          <h3 class="le-feedback-modal__rating-heading">{{ t("le-feedback-modal.rating-heading", { site }) }}</h3>
          <LeFeedbackModalStars v-model="rating" class="le-feedback-modal__stars" />
          <p v-if="hasRated" class="le-feedback-modal__rating-thanks">
            {{ t("le-feedback-modal.thanks-for-rating") }}
          </p>
        </section>

        <section v-if="hasRated" class="le-feedback-modal__section le-feedback-modal__section-comment">
          <LeHeading tag="h3" size="s">{{ commentHeading }}</LeHeading>
          <textarea
            v-model="comment"
            class="le-feedback-modal__input le-feedback-modal__comment-textarea"
            :placeholder="t('le-feedback-modal.comment-placeholder')"
            :has-error="ValidationHelper.displayError(commentMeta)"
          />
          <LeInput
            v-model="email"
            type="email"
            class="le-feedback-modal__input"
            :placeholder="t('le-feedback-modal.email-placeholder')"
            :has-error="ValidationHelper.immediateDisplayError(emailMeta)"
          />
          <label class="le-feedback-modal__contact-consent">
            <LeCheckbox v-model="canContact" />
            <i18n-t
              tag="span"
              keypath="le-feedback-modal.contact-consent-text"
              :class="{ 'le-feedback-modal__field-error': ValidationHelper.immediateDisplayError(canContactMeta) }"
            >
              <template #privacyPolicyLink>
                <a :href="`${language}/policies/`" target="_blank" class="le-feedback-modal__link">
                  {{ t("le-feedback-modal.privacy-policy") }}
                </a>
              </template>
            </i18n-t>
          </label>
        </section>
      </form>
    </template>

    <template v-if="hasRated" #buttons>
      <LeButton variant="outline" @click="onClose()">{{ t("le-feedback-modal.close") }}</LeButton>
      <LeButton type="submit" form="feedback-form" :disabled="!hasCommented">
        {{ t("le-feedback-modal.send-feedback") }}
      </LeButton>
    </template>
  </LeModal>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all.less";

.le-feedback-modal {
  &__section {
    display: flex;
    flex-direction: column;

    &-rating {
      align-items: center;
      gap: 24px;
      margin-bottom: 48px;
    }

    &-comment {
      gap: 12px;
    }
  }

  &__rating-heading {
    .body-text("m", "semi-bold");
  }

  &__rating-thanks {
    .body-text("s");
    margin: 0;
  }

  &__stars {
    display: flex;
    gap: 30px;
  }

  &__input {
    .body-text("m");
    padding: 8px 12px;
    border: 1px solid @colors[@secondary][600];
    border-radius: 4px;
    background: @colors[@neutrals][100];

    &::placeholder {
      opacity: 0.4;
      color: @colors[@neutrals][500];
    }
  }

  &__comment-textarea {
    min-height: 139px;
    resize: vertical;
  }

  &__contact-consent {
    display: flex;
    gap: 12px;
    margin: 0;
  }

  &__field-error {
    color: @colors[@error][600];
  }

  &__link {
    .body-text("m");
    text-decoration: underline;
  }
}
</style>
